import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Feather, PlusCircle, Loader2, BookOpen, Coins, Users } from "lucide-react";

import { _t, _locale } from "@/lib/i18n";
const AGE_OPTIONS = [
  { id: "all_ages", label: _t("Per tutti") },
  { id: "age_13", label: "13+" },
  { id: "age_16", label: "16+" },
  { id: "age_18", label: "18+" },
];

function NewChapter({ onCreated }) {
  const [title, setTitle] = useState("");
  const [synopsis, setSynopsis] = useState("");
  const [body, setBody] = useState("");
  const [tags, setTags] = useState("");
  const [age, setAge] = useState("all_ages");
  const [openAt, setOpenAt] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const d = new Date(); d.setDate(d.getDate() + 20);
    setOpenAt(d.toISOString().slice(0, 10));
  }, []);

  const submit = async () => {
    if (title.trim().length < 3 || synopsis.trim().length < 10 || body.length < 200) {
      toast.error(_t("Titolo, sinossi e testo (min 200 caratteri) sono obbligatori."));
      return;
    }
    setBusy(true);
    try {
      const iso = new Date(openAt + "T12:00:00Z").toISOString();
      const { data } = await api.post("/racconti/chapters", {
        title: title.trim(), synopsis: synopsis.trim(), body: body.trim(),
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
        age_declared: age, open_at: iso,
      });
      if (data.requires_admin_review) {
        toast.warning(_t("Il tuo capitolo è in revisione (AI suggerisce {0}).", data.age_ai_suggested));
      } else {
        toast.success(_t("Capitolo sigillato! Sarà annunciato alla community 14 giorni prima dell'apertura."));
      }
      setTitle(""); setSynopsis(""); setBody(""); setTags("");
      onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-3">
      <div className="flex items-center gap-2 text-xs text-white/50 uppercase tracking-widest">
        <PlusCircle className="w-3 h-3" />{" "}{_t("Nuovo capitolo")}</div>
      <input data-testid="ch-title" value={title} onChange={(e)=>setTitle(e.target.value)} maxLength={180}
             placeholder={_t("Titolo del capitolo")} className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3 text-white" />
      <textarea data-testid="ch-synopsis" value={synopsis} onChange={(e)=>setSynopsis(e.target.value)} rows={2} maxLength={600}
                placeholder={_t("Sinossi (max 600 caratteri)")} className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3 text-white text-sm" />
      <textarea data-testid="ch-body" value={body} onChange={(e)=>setBody(e.target.value)} rows={10} maxLength={40000}
                placeholder={_t("Testo completo (200 – 40.000 caratteri)")} className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3 text-white text-sm" />
      <div className="text-xs text-white/40 text-right">{body.length} / 40.000</div>
      <div className="flex flex-wrap gap-3">
        <input data-testid="ch-tags" value={tags} onChange={(e)=>setTags(e.target.value)} placeholder={_t("tag1, tag2, tag3")}
               className="flex-1 min-w-[200px] bg-black/20 border border-white/10 rounded-xl px-4 py-2 text-white text-sm" />
        <input data-testid="ch-open-at" type="date" value={openAt} onChange={(e)=>setOpenAt(e.target.value)}
               className="bg-black/20 border border-white/10 rounded-xl px-4 py-2 text-white text-sm" />
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-white/50">{_t("Età:")}</span>
        {AGE_OPTIONS.map((o) => (
          <button key={o.id} data-testid={`age-${o.id}`} onClick={() => setAge(o.id)}
                  className={`px-3 py-1 rounded-full text-xs border ${age === o.id ? "border-[#FF5722] text-[#FF5722]" : "border-white/15 text-white/60"}`}>
            {o.label}
          </button>
        ))}
      </div>
      <div className="flex justify-end">
        <button data-testid="ch-submit" onClick={submit} disabled={busy} className="btn-primary !py-2 !px-4 inline-flex items-center gap-2">{_t("{0} Sigilla capitolo", busy && <Loader2 className="w-4 h-4 animate-spin" />)}</button>
      </div>
    </section>
  );
}

export default function RaccontiAuthorDashboard() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [m, c] = await Promise.all([
        api.get("/racconti/me/author"),
        api.get("/racconti/chapters/mine"),
      ]);
      if (!m.data.is_author) { nav("/racconti/autore/registrati"); return; }
      setMe(m.data); setChapters(c.data.chapters);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (loading || !me) return <AppLayout><div className="text-white/50">{_t("Carico…")}</div></AppLayout>;

  return (
    <AppLayout>
      <div className="space-y-6">
        <header className="space-y-1">
          <h1 className="text-4xl text-white font-display flex items-center gap-3">
            <Feather className="w-9 h-9 text-amber-300" />{" "}{_t("Area Autore ·")}{" "}{me.pen_name}
          </h1>
          <p className="text-white/60 text-sm">{_t("Slug pubblico:")}{" "}<code className="text-cyan-300">{_t("/racconti/autore/{0}", me.slug)}</code></p>
        </header>

        <div className="grid gap-4 md:grid-cols-3">
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
            <div className="text-[10px] uppercase tracking-widest text-white/40 flex items-center gap-1"><BookOpen className="w-3 h-3" />{" "}{_t("Download totali")}</div>
            <div className="text-2xl text-white font-display">{me.downloads_total}</div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
            <div className="text-[10px] uppercase tracking-widest text-white/40 flex items-center gap-1"><Coins className="w-3 h-3" />{" "}{_t("Guadagno accumulato")}</div>
            <div className="text-2xl text-white font-display">€ {me.revenue_accumulated_eur.toFixed(4)}</div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
            <div className="text-[10px] uppercase tracking-widest text-white/40 flex items-center gap-1"><Users className="w-3 h-3" />{" "}{_t("Capitoli")}</div>
            <div className="text-2xl text-white font-display">{chapters.length}</div>
          </div>
        </div>

        <NewChapter onCreated={load} />

        <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-2">
          <h2 className="text-lg text-white font-display">{_t("I miei capitoli")}</h2>
          {chapters.length === 0 ? (
            <div className="text-white/50 text-sm">{_t("Ancora nessun capitolo.")}</div>
          ) : (
            <div className="space-y-2">
              {chapters.map((c) => (
                <div key={c.id} data-testid={`mine-${c.id}`} className="p-3 rounded-xl border border-white/10 bg-black/20 flex justify-between items-center">
                  <div>
                    <div className="text-white">{c.title}</div>
                    <div className="text-xs text-white/40">{_t("Stato:")}{" "}<span className="text-white/70">{c.state}</span>{" "}{_t("· Età:")}{" "}{c.age_final || c.age_declared || "?"}{" "}{_t("· Apertura:")}{" "}{c.open_at ? new Date(c.open_at).toLocaleDateString(_locale()) : "?"}
                    </div>
                  </div>
                  <div className="text-xs text-white/50 text-right">
                    <div>{_t("{0} DL", c.downloads_count)}</div>
                    <div>{_t("{0} recensioni", c.reviews_count)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </AppLayout>
  );
}
